import { theme, useResponsive } from "@product/scmp-sdk";
import type {
  EmbeddedSpeechifyExperience,
  SpeechifyPlayerConfig,
} from "https://storage.googleapis.com/speechify-api-cdn/speechifyapi.min.mjs";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { createContext, useCallback, useContext, useState } from "react";

import { ZIndexLayer } from "~/lib/styles";

import { SpeechSkipClassName } from "./consts";

type ContextValue = {
  articleSpeechifyPlayer: OrUndefined<EmbeddedSpeechifyExperience>;
  articleSpeechifyPlayerConfig: OrUndefined<Partial<SpeechifyPlayerConfig>>;
  handleClosePlayer?: () => void;
  setArticleSpeechifyPlayer?: Dispatch<SetStateAction<OrUndefined<EmbeddedSpeechifyExperience>>>;
  setArticleSpeechifyPlayerConfig?: (config: Partial<SpeechifyPlayerConfig>) => void;
};

const ArticleSpeechContext = createContext<ContextValue>({
  articleSpeechifyPlayer: undefined,
  articleSpeechifyPlayerConfig: undefined,
});

type Props = {
  children: ReactNode;
};

export const ArticleSpeechContextProvider: React.FunctionComponent<Props> = ({ children }) => {
  const [articleSpeechifyPlayer, setArticleSpeechifyPlayer] =
    useState<OrUndefined<EmbeddedSpeechifyExperience>>();

  const [articleSpeechifyPlayerConfig, setArticleSpeechifyPlayerConfig] =
    useState<Partial<SpeechifyPlayerConfig>>();

  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);

  const handleSetSpeechifyPlayerConfig = useCallback(
    (playerConfig: Partial<SpeechifyPlayerConfig>) =>
      setArticleSpeechifyPlayerConfig({
        customStyles: {
          playButton: {
            background: "radial-gradient(#ffffff 50%, transparent 50%)",
          },
          primaryColor: "#4585FF",
          widget: {
            borderRadius: "8px",
            bottom: isMobile ? "20px" : "40px",
            zIndex: `${ZIndexLayer.Drawer - 1}`,
          },
        },
        enableAutoScroll: false,
        enableHighlighting: false,
        ignoreElements(element) {
          return (
            element.tagName === "NOSCRIPT" ||
            element.tagName === "STYLE" ||
            element.tagName === "SCRIPT" ||
            element.classList.contains(SpeechSkipClassName)
          );
        },
        ...playerConfig,
        showFeedbackForm: false,
      }),
    [isMobile],
  );

  const handleClosePlayer = useCallback(() => {
    if (!articleSpeechifyPlayer) return;
    articleSpeechifyPlayer.pause();
    void articleSpeechifyPlayer?.unmount();
    setArticleSpeechifyPlayerConfig?.(undefined);
    setArticleSpeechifyPlayer?.(undefined);
  }, [articleSpeechifyPlayer]);

  return (
    <ArticleSpeechContext.Provider
      value={{
        articleSpeechifyPlayer,
        articleSpeechifyPlayerConfig,
        handleClosePlayer,
        setArticleSpeechifyPlayer,
        setArticleSpeechifyPlayerConfig: handleSetSpeechifyPlayerConfig,
      }}
    >
      {children}
    </ArticleSpeechContext.Provider>
  );
};
ArticleSpeechContextProvider.displayName = "ArticleSpeechContextProvider";

export const useArticleSpeechContext = () => useContext(ArticleSpeechContext);
